import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  AlertTriangle, 
  FileText, 
  ExternalLink, 
  RefreshCw,
  Clock,
  Building2,
  TrendingUp,
  Zap
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LivePolicyWidget = ({ language = 'en', maxItems = 5, showRefresh = true }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchLivePolicies = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/federal-register/environmental-justice`, {
        params: { days: 14, per_page: maxItems }
      });
      setPolicies(response.data.results || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch live policies:', err);
      setError(language === 'es' ? 'Error al cargar políticas' : 'Failed to load policies');
      // Fallback to public inspection docs
      try {
        const fallbackResponse = await axios.get(`${API}/federal-register/public-inspection`, {
          params: { per_page: maxItems }
        });
        setPolicies(fallbackResponse.data.results || []);
        setLastUpdated(new Date());
        setError(null);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLivePolicies();
    // Refresh every 5 minutes
    const interval = setInterval(fetchLivePolicies, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [maxItems]);

  const getRiskColor = (score) => {
    if (score >= 70) return 'bg-red-500';
    if (score >= 40) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return language === 'es' ? 'Alto Riesgo' : 'High Risk';
    if (score >= 40) return language === 'es' ? 'Riesgo Medio' : 'Medium Risk';
    return language === 'es' ? 'Bajo Riesgo' : 'Low Risk';
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return language === 'es' ? 'Hoy' : 'Today';
    if (diffDays === 1) return language === 'es' ? 'Ayer' : 'Yesterday';
    if (diffDays < 7) return language === 'es' ? `Hace ${diffDays} días` : `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Card className="w-full" data-testid="live-policy-widget-loading">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-6 w-48" />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border/50">
              <Skeleton className="h-2 w-2 rounded-full mt-2" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full overflow-hidden" data-testid="live-policy-widget">
      <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Zap className="h-5 w-5 text-primary" />
              <span className="absolute -top-1 -right-1 h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
            </div>
            <CardTitle className="text-lg font-semibold">
              {language === 'es' ? 'Alertas en Vivo del Registro Federal' : 'Live Federal Register Alerts'}
            </CardTitle>
          </div>
          {showRefresh && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchLivePolicies}
              className="h-8 w-8 p-0"
              data-testid="refresh-policies-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        {lastUpdated && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {language === 'es' ? 'Actualizado' : 'Updated'}: {lastUpdated.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      <CardContent className="pt-3">
        {error ? (
          <div className="text-center py-6 text-muted-foreground">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
            <p className="text-sm">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchLivePolicies} className="mt-3">
              {language === 'es' ? 'Reintentar' : 'Retry'}
            </Button>
          </div>
        ) : policies.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {language === 'es' 
                ? 'No hay nuevas políticas ambientales' 
                : 'No new environmental policies'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {policies.slice(0, maxItems).map((policy, index) => (
              <div
                key={policy.id || index}
                className="group flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:border-primary/30 hover:bg-accent/5 transition-all cursor-pointer"
                data-testid={`live-policy-${index}`}
                onClick={() => policy.source_url && window.open(policy.source_url, '_blank')}
              >
                <div className={`h-2 w-2 rounded-full mt-2 ${getRiskColor(policy.risk_score || 50)}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                      {policy.title}
                    </h4>
                    {policy.source_url && (
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Badge variant="outline" className="text-xs h-5 px-1.5">
                      {policy.document_type || 'Document'}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {policy.agencies?.[0]?.substring(0, 20) || 'Federal'}
                    </span>
                    {policy.publication_date && (
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(policy.publication_date)}
                      </span>
                    )}
                  </div>
                  {policy.is_early_warning && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <TrendingUp className="h-3 w-3 text-amber-500" />
                      <span className="text-xs text-amber-600 font-medium">
                        {language === 'es' ? 'Alerta Temprana' : 'Early Warning'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {policies.length > 0 && (
              <div className="pt-2 border-t border-border/50 mt-3">
                <p className="text-xs text-center text-muted-foreground">
                  {language === 'es' 
                    ? `Mostrando ${Math.min(maxItems, policies.length)} de políticas recientes`
                    : `Showing ${Math.min(maxItems, policies.length)} recent policies`}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LivePolicyWidget;
